import { useCallback, useId, useRef, type ReactNode, type RefObject } from 'react';
import { Box, Modal, type SxProps, type Theme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RwButton from './RwButton';

export type RwModalSize = 's' | 'm' | 'l';

/**
 * Title step. `default` is the DS H4 (`.rw-modal__title`); `large` / `small` are explicit size-token steps
 * for a frame that shows a 24px or a 14px title, so the title does not follow the compact type scale.
 */
export type RwModalTitleSize = 'default' | 'large' | 'small';

/**
 * The `&&` selector doubles the generated class so these declarations beat the DS's single-class rules
 * (`.rw-modal__title`, `.rw-modal`) whatever order Emotion and `rw-ds.css` end up in the document, in
 * standalone and when this remote is embedded.
 */
const TITLE_SX: Record<RwModalTitleSize, SxProps<Theme> | undefined> = {
  default: undefined,
  large: { '&&': { fontSize: 'var(--size-24)', lineHeight: 'var(--size-32)' } },
  small: { '&&': { fontSize: 'var(--size-14)', lineHeight: 'var(--size-20)' } }
};

export interface RwModalProps {
  open: boolean;
  onClose: () => void;
  /** Modal title — rendered as the DS H4 modal title. */
  title: string;
  /** Optional context line under the title — `.rw-modal__context` (Body, NOT greyed, rw-ds-overlays §6.3). */
  context?: string;
  /** Size step per rw-ds-overlays §4: s ≈ 400 · m ≈ 512 (default) · l ≈ 720. */
  size?: RwModalSize;
  /**
   * Exact width for a frame that sits between the DS steps (e.g. 448 / 600). Any CSS length — express it
   * with size tokens, `calc(var(--size-400) + var(--size-48))` — set on the DS's own `--_modal-w` custom
   * property, so the DS `min(…, 100vw − gutter)` clamp still applies. Wins over `size`.
   */
  width?: string;
  /** Title step; see `RwModalTitleSize`. Default `default` (the DS H4). */
  titleSize?: RwModalTitleSize;
  /**
   * The control that takes focus when the dialog opens (the first field, or Cancel on a confirmation).
   * MUI's focus trap parks focus on the `.rw-overlay` wrapper instead when nothing inside holds it —
   * on open, and again when a closing menu hands focus back to its trigger — and `autoFocus` on a child
   * loses that race. The wrapper therefore forwards its own focus to this element.
   */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /**
   * Show the header close ✕. Default true. This only hides the button: Escape still calls `onClose`, so a
   * caller that must not lose the dialog (a submit in flight, a dirty form) has to guard `onClose` itself.
   */
  dismissable?: boolean;
  /** Footer actions (right-anchored, Secondary left of Primary). */
  footer?: ReactNode;
  children: ReactNode;
  /**
   * `data-testid` on the `.rw-modal` dialog root (repo adaptation — see `DS_VENDOR.md`). This
   * repo has no `tid()` helper (`.cursor/rules/test-id-naming-rules.mdc` names a file that does
   * not exist here) and writes literal `data-testid` values everywhere, so a consumer that needs
   * to target its own modal instance (rather than relying on `getByRole('dialog', { name })`)
   * needs somewhere to pass one.
   */
  testId?: string;
  /**
   * Id of a description element the CALLER renders (inside `children`, typically visually
   * hidden when the design has no visible helper copy) — wired onto the dialog as
   * `aria-describedby` (repo adaptation, `DS_VENDOR.md`). `.cursor/rules/a11y-rules.mdc`:
   * "When there is descriptive content, set `aria-describedby` to the helper/description
   * element." `context` alone cannot satisfy this when the design calls for NO visible helper
   * text (e.g. Figma `bl.new-block.empty`, `7:60`: "no helper text") — this covers that case.
   */
  descriptionId?: string;
  /**
   * Id for the visible `context` line, so a caller can point `descriptionId` /
   * `aria-describedby` at the helper the user actually sees.
   */
  contextId?: string;
  /**
   * `data-testid` on the header ✕ button (repo adaptation, same reasoning as `testId` above).
   * Optional and additive — omitting it keeps every existing caller's markup unchanged.
   */
  closeTestId?: string;
}

const SIZE_CLASS: Record<RwModalSize, string> = {
  s: 'rw-modal--s',
  m: '',
  l: 'rw-modal--l'
};

/**
 * RwModal — the DS overlay bridge (rw-ds-overlays §6). Renders the canonical
 * `.rw-overlay` / `.rw-modal` markup (scrim, xl elevation, radius 0, H4 title,
 * 24/16/32 spacing model — all from `rw-ds.css`), using MUI `Modal` only for the
 * a11y machinery (focus-trap, portal, scroll-lock, Esc-to-close). MUI's own backdrop
 * is suppressed; `.rw-overlay` is the scrim. Formik/consumer context crosses the
 * portal, so footer actions can read form state without a wrapping <form>.
 *
 * Not wired: scrim-click-to-close (avoids silent discard of a dirty form — the
 * unsaved-changes guard is a separate follow-up); sticky-footer scroll edge.
 */
export default function RwModal({
  open,
  onClose,
  title,
  context,
  size = 'm',
  width,
  titleSize = 'default',
  initialFocusRef,
  dismissable = true,
  footer,
  children,
  testId,
  descriptionId,
  contextId,
  closeTestId
}: RwModalProps) {
  const titleId = useId();
  const sizeClass = SIZE_CLASS[size];
  const dialogSx: SxProps<Theme> | undefined = width ? { '&&': { '--_modal-w': width } } : undefined;

  // A native listener on the wrapper (not a React `onFocus`): the wrapper is not an interactive element, it
  // only receives programmatic focus from the focus trap, and this keeps it free of event props.
  // The hand-off is deferred one microtask on purpose: the trap records the element that held focus before the
  // dialog (`relatedTarget` of its first focus event) in a React handler that runs AFTER this native one, and
  // moving focus synchronously would make it record the wrapper itself — so Escape/Cancel could not give focus
  // back to the opener.
  const detachOverlayFocus = useRef<(() => void) | null>(null);
  const forwardOverlayFocus = useCallback(
    (node: HTMLDivElement | null) => {
      detachOverlayFocus.current?.();
      detachOverlayFocus.current = null;
      if (!node || !initialFocusRef) return;
      const onFocusIn = (event: Event) => {
        if (event.target === node) queueMicrotask(() => initialFocusRef.current?.focus());
      };
      node.addEventListener('focusin', onFocusIn);
      detachOverlayFocus.current = () => node.removeEventListener('focusin', onFocusIn);
    },
    [initialFocusRef]
  );

  return (
    <Modal open={open} onClose={() => onClose()} hideBackdrop aria-labelledby={titleId} aria-describedby={descriptionId}>
      <div className="rw-overlay" ref={forwardOverlayFocus}>
        <Box
          className={sizeClass ? `rw-modal ${sizeClass}` : 'rw-modal'}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          sx={dialogSx}
          data-testid={testId}
        >
          <div className="rw-modal__header">
            <div className="rw-modal__header-row">
              <Box component="h2" id={titleId} className="rw-modal__title" sx={TITLE_SX[titleSize]}>
                {title}
              </Box>
              {dismissable ? (
                <RwButton iconOnly tier="plain-neutral" size="sm" aria-label="Close" onClick={onClose} data-testid={closeTestId}>
                  <CloseIcon />
                </RwButton>
              ) : null}
            </div>
            {context ? (
              <p id={contextId} className="rw-modal__context">
                {context}
              </p>
            ) : null}
          </div>
          <div className="rw-modal__body">{children}</div>
          {footer ? <div className="rw-modal__footer">{footer}</div> : null}
        </Box>
      </div>
    </Modal>
  );
}
